import {
  usePowerSync,
  useQuery,
  useStatus,
  useSuspenseQuery,
} from '@powersync/react';
import {Database} from '../powersyncSchema';
import {
  AnimalType,
  BodyPart,
  CertificationType,
  City,
  Country,
  ClassificationUnit,
  Crew,
  Department,
  Disposition,
  Document,
  DocumentType,
  Equipment,
  EquipmentHeavyProfile,
  EquipmentOtherProfile,
  EquipmentVehicleProfile,
  EquipmentProfileType,
  EquipmentType,
  HazardType,
  IncidentClassification,
  IncidentType,
  IncidentProbability,
  Job,
  Location,
  LocationType,
  Mechanism,
  MeetingType,
  Nature,
  OIICS_BodyPart,
  OIICS_Nature,
  OIICS_Source,
  OperatingCondition,
  Organisation,
  OrgContractorNotificationRecipient,
  OrgRelation,
  OrgStorage,
  OrgTemplateGroup,
  PeopleType,
  Person,
  Permission,
  Project_location,
  Project,
  ProvState,
  Role,
  SeverityLevel,
  Shift,
  Task,
  TaskType,
  TemplateCategory,
  TemplateGroup,
  Template_templateCategory,
  Template_TemplateType,
  Template_Type,
  Template,
  TemplateVersion_pages,
  TemplateVersion_conditions,
  TemplateVersion,
  TemplateVersionsConversion_conversion,
  TemplateVersionsConversion,
  TrainingType,
  SQLUser,
  SQLUser_statusObj,
  UserStatus,
  SubmissionLink,
  SubmissionStatus,
  Submission_answer,
  Submission_submissionStatus,
  Submission_templateType,
  Submission_templateVersion,
  Submission,
  SubmissionBundle,
  SubmissionBundle_submissions,
  SubmissionBundle_answers,
  SubmissionBundle_templateType,
  SubmissionBundle_templateVersion,
  SubmissionRelation,
  SubmissionTraining_answer,
  SubmissionTraining_certificateType,
  SubmissionTraining_person,
  SubmissionTraining_submissionStatus,
  SubmissionTraining_trainingType,
  SubmissionTraining_templateType,
  SubmissionTraining_templateVersion,
  SubmissionTraining,
  UserCustomDatum,
} from '../Models/PowerSyncModels/models';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import React from 'react';

type PowerSyncDataProviderProps = {
  children: React.ReactNode;
};

type PowerSyncDataContextValue = {
  animalTypes: AnimalType[];
  bodyParts: BodyPart[];
  certificationTypes: CertificationType[];
  cities: City[];
  countries: Country[];
  classificationUnits: ClassificationUnit[];
  crews: Crew[];
  departments: Department[];
  dispositions: Disposition[];
  documents: Document[];
  documentTypes: DocumentType[];
  equipmentHeavyProfiles: EquipmentHeavyProfile[];
  equipmentOtherProfiles: EquipmentOtherProfile[];
  equipmentProfileTypes: EquipmentProfileType[];
  equipments: Equipment[];
  equipmentTypes: EquipmentType[];
  equipmentVehicleProfiles: EquipmentVehicleProfile[];
  hazardTypes: HazardType[];
  incidentClassifications: IncidentClassification[];
  incidentProbabilities: IncidentProbability[];
  incidentTypes: IncidentType[];
  jobs: Job[];
  locations: Location[];
  locationTypes: LocationType[];
  mechanisms: Mechanism[];
  meetingTypes: MeetingType[];
  natures: Nature[];
  OIICS_BodyParts: OIICS_BodyPart[];
  OIICS_Natures: OIICS_Nature[];
  OIICS_Sources: OIICS_Source[];
  operatingConditions: OperatingCondition[];
  organisations: Organisation[];
  orgContractorNotificationRecipients: OrgContractorNotificationRecipient[];
  orgRelations: OrgRelation[];
  orgStorages: OrgStorage[];
  orgTemplateGroups: OrgTemplateGroup[];
  people: Person[];
  peopleTypes: PeopleType[];
  permissions: Permission[];
  projects: Project[];
  provStates: ProvState[];
  roles: Role[];
  severityLevels: SeverityLevel[];
  shifts: Shift[];
  SQLUsers: SQLUser[];
  submissionBundles: SubmissionBundle[];
  submissionRelations: SubmissionRelation[];
  submissions: Submission[];
  submissionStatuses: SubmissionStatus[];
  submissionTrainings: SubmissionTraining[];
  tasks: Task[];
  taskTypes: TaskType[];
  templateCategories: TemplateCategory[];
  templateGroups: TemplateGroup[];
  templates: Template[];
  templateTypes: Template_Type[];
  templateVersionConversions: TemplateVersionsConversion[];
  templateVersions: TemplateVersion[];
  trainingTypes: TrainingType[];
  userStatuses: UserStatus[];
  submissionLinks: SubmissionLink[];
  getPerson(personId: string): Promise<Person>;
  getProject(projectId: string): Promise<Project>;
  getEquipmentVehicle(id: string): Promise<EquipmentVehicleProfile>;
  getEquipmentHeavy(id: string): Promise<EquipmentHeavyProfile>;
  getEquipmentOther(id: string): Promise<EquipmentOtherProfile>;
  getTemplate(id: string): Promise<Template>;
  getTemplateVersion(id: string): Promise<TemplateVersion>;
  getSubmissionBundle(submissionBundleId: string): Promise<SubmissionBundle>;
  getSubmission(submissionId: string): Promise<Submission>;
  getSubmissionTraining(
    submissionTrainingId: string,
  ): Promise<SubmissionTraining>;
  existSubmissionInBundle: (submissionId: string) => boolean;
  powersyncReady: boolean;
};

const PowerSyncDataContext = React.createContext<PowerSyncDataContextValue>(
  {} as PowerSyncDataContextValue,
);

//Mongo Permissions table has list of permissions with an array of roles that has access to that permission
const PowerSyncDataProvider: React.FunctionComponent<
  PowerSyncDataProviderProps
> = ({children}): React.ReactElement => {
  const powerSync = usePowerSync();
  const powersyncStatus = useStatus();

  const {data: animalTypes} = useQuery<Database['AnimalTypes']>(
    'SELECT * FROM AnimalTypes ',
  );
  const {data: bodyParts} = useQuery<Database['BodyParts']>(
    'SELECT * FROM BodyParts ',
  );
  const {data: certificationTypes} = useQuery<Database['CertificationTypes']>(
    'SELECT * FROM CertificationTypes ',
  );
  const {data: cities} = useQuery<Database['Cities']>('SELECT * FROM Cities ');
  const {data: classificationUnits} = useQuery<Database['ClassificationUnits']>(
    'SELECT * FROM ClassificationUnits ',
  );
  const {data: crews} = useQuery<Database['Crews']>('SELECT * FROM Crews ');
  const {data: departments} = useQuery<Database['Departments']>(
    'SELECT * FROM Departments ',
  );
  const {data: dispositions} = useQuery<Database['Dispositions']>(
    'SELECT * FROM Dispositions ',
  );
  const {data: documents, isLoading: loadingDocuments} = useQuery<
    Database['Documents']
  >('SELECT * FROM Documents ');
  const {data: documentTypes, isLoading: loadingDocumentTypes} = useQuery<
    Database['DocumentTypes']
  >('SELECT * FROM DocumentTypes ');
  const {
    data: equipmentHeavyProfiles,
    isLoading: loadingEquipmentHeavyProfiles,
  } = useQuery<Database['EquipmentHeavyProfiles']>(
    'SELECT * FROM EquipmentHeavyProfiles ',
  );
  const {
    data: equipmentOtherProfiles,
    isLoading: loadingEquipmentOtherProfiles,
  } = useQuery<Database['EquipmentOtherProfiles']>(
    'SELECT * FROM EquipmentOtherProfiles ',
  );
  const {data: equipmentProfileTypes} = useQuery<
    Database['EquipmentProfileTypes']
  >('SELECT * FROM EquipmentProfileTypes ');

  const {data: equipments} = useQuery<Database['Equipments']>(
    'SELECT * FROM Equipments ',
  );
  const {data: equipmentTypes} = useQuery<Database['EquipmentTypes']>(
    'SELECT * FROM EquipmentTypes ',
  );
  const {
    data: equipmentVehicleProfiles,
    isLoading: loadingEquipmentVehicleProfiles,
  } = useQuery<Database['EquipmentVehicleProfiles']>(
    'SELECT * FROM EquipmentVehicleProfiles ',
  );
  const {data: hazardTypes} = useQuery<Database['HazardTypes']>(
    'SELECT * FROM HazardTypes ',
  );
  const {data: incidentClassifications} = useQuery<
    Database['IncidentClassifications']
  >('SELECT * FROM IncidentClassifications ');
  const {data: incidentProbabilities} = useQuery<
    Database['IncidentProbabilities']
  >('SELECT * FROM IncidentProbabilities ');
  const {data: incidentTypes} = useQuery<Database['IncidentTypes']>(
    'SELECT * FROM IncidentTypes ',
  );
  const {data: jobs} = useQuery<Database['Jobs']>('SELECT * FROM Jobs ');
  const {data: locations} = useQuery<Database['Locations']>(
    'SELECT * FROM Locations ',
  );
  const {data: locationTypes} = useQuery<Database['LocationTypes']>(
    'SELECT * FROM LocationTypes ',
  );
  const {data: mechanisms} = useQuery<Database['Mechanisms']>(
    'SELECT * FROM Mechanisms ',
  );
  const {data: meetingTypes} = useQuery<Database['MeetingTypes']>(
    'SELECT * FROM MeetingTypes ',
  );
  const {data: natures} = useQuery<Database['Natures']>(
    'SELECT * FROM Natures ',
  );
  const {data: OIICS_BodyParts} = useQuery<Database['OIICS_BodyParts']>(
    'SELECT * FROM OIICS_BodyParts ',
  );
  const {data: OIICS_Natures} = useQuery<Database['OIICS_Natures']>(
    'SELECT * FROM OIICS_Natures ',
  );
  const {data: OIICS_Sources} = useQuery<Database['OIICS_Sources']>(
    'SELECT * FROM OIICS_Sources ',
  );
  const {data: operatingConditions} = useQuery<Database['OperatingConditions']>(
    'SELECT * FROM OperatingConditions ',
  );
  const {data: organisations} = useQuery<Database['Organisations']>(
    'SELECT * FROM Organisations ',
  );
  const {data: orgContractorNotificationRecipients} = useQuery<
    Database['OrgContractorNotificationRecipients']
  >('SELECT * FROM OrgContractorNotificationRecipients ');
  const {data: orgRelations} = useQuery<Database['OrgRelations']>(
    'SELECT * FROM OrgRelations ',
  );
  const {data: orgStorages} = useQuery<Database['OrgStorage']>(
    'SELECT * FROM OrgStorage ',
  );
  const {data: orgTemplateGroups} = useQuery<Database['OrgTemplateGroups']>(
    'SELECT * FROM OrgTemplateGroups ',
  );
  const {data: people, isLoading: loadingPeople} = useQuery<Database['People']>(
    'SELECT * FROM People ',
  );
  const {data: peopleTypes} = useQuery<Database['PeopleTypes']>(
    'SELECT * FROM PeopleTypes ',
  );
  const {data: permissions, isLoading: loadingPermissions} = useQuery<
    Database['Permissions']
  >('SELECT * FROM Permissions ');
  const {data: projects} = useQuery<Database['Projects']>(
    'SELECT * FROM Projects ',
  );
  const {data: provStates} = useQuery<Database['ProvStates']>(
    'SELECT * FROM ProvStates ',
  );
  const {data: roles} = useQuery<Database['Roles']>('SELECT * FROM Roles ');
  const {data: severityLevels} = useQuery<Database['SeverityLevels']>(
    'SELECT * FROM SeverityLevels ',
  );
  const {data: shifts} = useQuery<Database['Shifts']>('SELECT * FROM Shifts ');
  const {data: SQLUsers} = useQuery<Database['SQLUsers']>(
    'SELECT * FROM SQLUsers ',
  );
  const {data: submissionBundles, isLoading: loadingSubmissionBundles} =
    useQuery<Database['SubmissionBundles']>('SELECT * FROM SubmissionBundles ');
  const {data: submissionRelations} = useQuery<Database['SubmissionRelations']>(
    'SELECT * FROM SubmissionRelations ',
  );
  const {data: submissions, isLoading: loadingSubmissions} = useQuery<
    Database['Submissions']
  >('SELECT * FROM Submissions ');
  const {data: submissionStatuses, isLoading: loadingSubmissionStatuses} =
    useQuery<Database['SubmissionStatuses']>(
      'SELECT * FROM SubmissionStatuses ',
    );
  const {data: submissionTrainings, isLoading: loadingSubmissionTrainings} =
    useQuery<Database['SubmissionTrainings']>(
      'SELECT * FROM SubmissionTrainings ',
    );
  const {data: tasks} = useQuery<Database['Tasks']>('SELECT * FROM Tasks ');
  const {data: taskTypes} = useQuery<Database['TaskTypes']>(
    'SELECT * FROM TaskTypes ',
  );
  const {data: templateCategories} = useQuery<Database['TemplateCategories']>(
    'SELECT * FROM TemplateCategories ',
  );
  const {data: templateGroups} = useQuery<Database['TemplateGroups']>(
    'SELECT * FROM TemplateGroups ',
  );
  const {data: templates, isLoading: loadingTemplates} = useQuery<
    Database['Templates']
  >('SELECT * FROM Templates ');
  const {data: templateTypes} = useQuery<Database['TemplateTypes']>(
    'SELECT * FROM TemplateTypes ',
  );
  const {data: templateVersionConversions} = useQuery<
    Database['TemplateVersionConversions']
  >('SELECT * FROM TemplateVersionConversions ');
  const {data: templateVersions, isLoading: loadingTemplateVersions} = useQuery<
    Database['TemplateVersions']
  >('SELECT * FROM TemplateVersions ');
  const {data: trainingTypes} = useQuery<Database['TrainingTypes']>(
    'SELECT * FROM TrainingTypes ',
  );
  const {data: userStatuses} = useQuery<Database['UserStatuses']>(
    'SELECT * FROM UserStatuses ',
  );
  const {data: submissionLinks} = useQuery<Database['SubmissionLinks']>(
    'SELECT * FROM SubmissionLinks ',
  );

  const mappedAnimalTypes = useMemo(() => {
    return (
      animalTypes.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? []
    );
  }, [animalTypes]);

  const mappedBodyParts = useMemo(() => {
    return (
      bodyParts.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        sortOrder: x.sortOrder!,
      })) ?? []
    );
  }, [bodyParts]);

  const mappedCertificationTypes = useMemo(() => {
    return (
      certificationTypes.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? []
    );
  }, [certificationTypes]);

  const {data: _countries} = useQuery<Database['Countries']>(
    'SELECT * FROM Countries',
  );

  const countries = useMemo<Country[]>(() => {
    return (
      _countries.map(x => ({
        _id: x._id!,
        countryName: x.countryName,
        countryCode: x.countryCode,
        sortOrder: x.sortOrder!,
        SQLServerId: x.SQLServerId!,
      })) ?? []
    );
  }, [_countries]);

  const mappedCities = useMemo(
    () =>
      cities.map(x => ({
        _id: x._id!,
        cityName: x.cityName!,
        provStateSQLServerId: x.provStateSQLServerId!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [cities],
  );

  const mappedClassificationUnits = useMemo(
    () =>
      classificationUnits.map(x => ({
        _id: x._id!,
        name: x.name!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [classificationUnits],
  );

  const mappedCrews = useMemo(
    () =>
      crews.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [crews],
  );

  const mappedDepartments = useMemo(
    () =>
      departments.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [departments],
  );

  const mappedDispositions = useMemo(
    () =>
      dispositions.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [dispositions],
  );

  const mappedDocuments = useMemo(
    () =>
      documents.map(x => ({
        _id: x._id!,
        SQLServerId: x.SQLServerId!,
        name: x.name!,
        partition: x.partition!,
        documentTypeId: x.documentTypeId!,
        showOffline: !!x.showOffline,
        showCompanyWide: !!x.showCompanyWide,
        attachmentJSON: x.attachmentJSON!,
        createdBy: x.createdBy!,
      })) ?? [],
    [documents],
  );

  const mappedDocumentTypes = useMemo(
    () =>
      documentTypes.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [documentTypes],
  );

  const mappedEquipments = useMemo(
    () =>
      equipments.map(x => ({
        _id: x._id!,
        name: x.name!,
        equipmentTypeSQLServerId: x.equipmentTypeSQLServerId!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [equipments],
  );

  const mappedEquipmentHeavyProfiles = useMemo(
    () =>
      equipmentHeavyProfiles.map(x => ({
        _id: x._id!,
        name: x.name!,
        number: x.number!,
        hoursUsed: x.hoursUsed!,
        description: x.description!,
        ownershipType: x.ownershipType!,
        status: x.status!,
        photo: x.photo!,
        photos: x.photos!,
        partition: x.partition!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [equipmentHeavyProfiles],
  );

  const mappedEquipmentOtherProfiles = useMemo(
    () =>
      equipmentOtherProfiles.map(x => ({
        _id: x._id!,
        name: x.name!,
        number: x.number!,
        description: x.description!,
        ownershipType: x.ownershipType!,
        status: x.status!,
        photo: x.photo!,
        photos: x.photos!,
        partition: x.partition!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [equipmentOtherProfiles],
  );

  const mappedEquipmentVehicleProfiles = useMemo(
    () =>
      equipmentVehicleProfiles.map(x => ({
        _id: x._id!,
        name: x.name!,
        number: x.number!,
        description: x.description!,
        licensePlate: x.licensePlate!,
        distance: x.distance!,
        ownershipType: x.ownershipType!,
        status: x.status!,
        photo: x.photo!,
        photos: x.photos!,
        partition: x.partition!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [equipmentVehicleProfiles],
  );

  const mappedEquipmentProfileTypes = useMemo(
    () =>
      equipmentProfileTypes.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        sortOrder: x.sortOrder!,
      })) ?? [],
    [equipmentProfileTypes],
  );

  const mappedEquipmentTypes = useMemo(
    () =>
      equipmentTypes.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        sortOrder: x.sortOrder!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [equipmentTypes],
  );

  const mappedHazardTypes = useMemo(
    () =>
      hazardTypes.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [hazardTypes],
  );

  const mappedIncidentClassifications = useMemo(
    () =>
      incidentClassifications.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        incidentTypeSQLServerId: x.incidentTypeSQLServerId!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [incidentClassifications],
  );

  const mappedIncidentTypes = useMemo(
    () =>
      incidentTypes.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [incidentTypes],
  );

  const mappedIncidentProbabilities = useMemo(
    () =>
      incidentProbabilities.map(x => ({
        _id: x._id!,
        name: x.name!,
        value: x.value!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [incidentProbabilities],
  );

  const mappedJobs = useMemo(
    () =>
      jobs.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [jobs],
  );

  const mappedLocations = useMemo(
    () =>
      locations.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [locations],
  );

  const mappedLocationTypes = useMemo(
    () =>
      locationTypes.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [locationTypes],
  );

  const mappedMechanisms = useMemo(
    () =>
      mechanisms.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [mechanisms],
  );

  const mappedMeetingTypes = useMemo(
    () =>
      meetingTypes.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
      })) ?? [],
    [meetingTypes],
  );

  const mappedNatures = useMemo(
    () =>
      natures.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [natures],
  );

  const mappedOIICS_BodyParts = useMemo(
    () =>
      OIICS_BodyParts.map(x => ({
        _id: x._id!,
        name: x.name!,
        oiics_id: x._id!,
        parentid: x.parentid!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [OIICS_BodyParts],
  );

  const mappedOIICS_Natures = useMemo(
    () =>
      OIICS_Natures.map(x => ({
        _id: x._id!,
        name: x.name!,
        oiics_id: x._id!,
        parentid: x.parentid!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [OIICS_Natures],
  );

  const mappedOIICS_Sources = useMemo(
    () =>
      OIICS_Sources.map(x => ({
        _id: x._id!,
        createDateTimeStamp: x.createDateTimeStamp!,
        name: x.name!,
        oiics_id: x._id!,
        parentid: x.parentid!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [OIICS_Sources],
  );

  const mappedOperatingConditions = useMemo(
    () =>
      operatingConditions.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
      })) ?? [],
    [operatingConditions],
  );

  const mappedOrganisations = useMemo(
    () =>
      organisations.map(x => ({
        _id: x._id!,
        name: x.name!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        CORNumber: x.CORNumber!,
        SAFENumber: x.SAFENumber!,
        WSBCNumber: x.WSBCNumber!,
        WSBCUnitsJSON: x.WSBCUnitsJSON!,
        mainActivities: x.mainActivities!,
        SQLServerId: x.SQLServerId!,
        headOfficeLocationJSON: x.headOfficeLocationJSON!,
        mailingLocationJSON: x.mailingLocationJSON!,
        primaryContactJSON: x.primaryContactJSON!,
        auditContactJSON: x.auditContactJSON!,
        safetyContactJSON: x.safetyContactJSON!,
        tradeName: x.tradeName!,
        ownersJSON: x.ownersJSON!,
        dataHubVersion: x.dataHubVersion!,
        OrgTagPublic: x.OrgTagPublic!,
      })) ?? [],
    [organisations],
  );

  const mappedOrgContractorNotificationRecipients = useMemo(
    () =>
      orgContractorNotificationRecipients.map(x => ({
        _id: x._id!,
        SQLServerId: x.SQLServerId!,
        SQLServerPersonId: x.SQLServerPersonId!,
        isDeleted: !!x.isDeleted,
        orgId: x.orgId!,
        firstName: x.firstName!,
        lastName: x.lastName!,
        email: x.email!,
      })) ?? [],
    [orgContractorNotificationRecipients],
  );

  const mappedOrgRelations = useMemo(
    () =>
      orgRelations.map(x => ({
        _id: x._id!,
        SQLServerId: x.SQLServerId!,
        childName: x.childName!,
        childOrgId: x.childOrgId!,
        parentName: x.parentName!,
        parentOrgId: x.parentOrgId!,
        isDeleted: !!x.isDeleted,
      })) ?? [],
    [orgRelations],
  );

  const mappedOrgStorages = useMemo(
    () =>
      orgStorages.map(x => ({
        _id: x._id!,
        size: x.size!,
        partition: x.partition!,
      })) ?? [],
    [orgStorages],
  );

  const mappedOrgTemplateGroups = useMemo(
    () =>
      orgTemplateGroups.map(x => ({
        _id: x._id!,
        partition: x.partition!,
        templateGroupIds: x.templateGroupIds
          ? (JSON.parse(x.templateGroupIds!) as string[])
          : [],
      })) ?? [],
    [orgTemplateGroups],
  );

  const mappedPeopleTypes = useMemo(
    () =>
      peopleTypes.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [peopleTypes],
  );

  const mappedPeople = useMemo(
    () =>
      people.map(x => ({
        _id: x._id!,
        firstName: x.firstName!,
        lastName: x.lastName!,
        email: x.email!,
        phone: x.phone!,
        fax: x.fax!,
        phoneDirect: x.phoneDirect!,
        supervisorId: x.supervisorId!,
        supervisorSQLServerId: x.supervisorSQLServerId!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        SQLServerId: x.SQLServerId!,
        dataHubVersion: x.dataHubVersion!,
        jobId: x.jobId!,
        jobSQLServerId: x.jobSQLServerId!,
        peopleTypeId: x.peopleTypeId!,
        peopleTypeSQLServerId: x.peopleTypeSQLServerId!,
        photo: x.photo!,
        photoThumbnail: x.photoThumbnail!,
        hireDate: x.hireDate ? new Date(x.hireDate!) : null,
        inactiveDate: x.inactiveDate ? new Date(x.inactiveDate!) : null,
        employeeNumber: x.employeeNumber!,
      })) ?? [],
    [people],
  );

  const mappedPermissions = useMemo(
    () =>
      permissions.map(x => ({
        _id: x._id!,
        name: x.name!,
        roles: x.roles ? (JSON.parse(x.roles) as string[]) : [],
      })) ?? [],
    [permissions],
  );

  const mappedProjects = useMemo(
    () =>
      projects.map(x => ({
        _id: x._id!,
        name: x.name!,
        projectManagerId: x.projectManagerId!,
        projectManagerSQLServerId: x.projectManagerSQLServerId!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        location: x.location
          ? (JSON.parse(x.location) as Project_location)
          : null!,
        activeStartDate: x.activeStartDate ? new Date(x.activeStartDate) : null,
        activeEndDate: x.activeEndDate ? new Date(x.activeEndDate) : null,
        SQLServerId: x.SQLServerId!,
        safetyResourceId: x.safetyResourceId!,
        safetyResourceSQLServerId: x.safetyResourceSQLServerId!,
        siteSupervisorId: x.siteSupervisorId!,
        siteSupervisorSQLServerId: x.siteSupervisorSQLServerId!,
        workers: x.workers ? (JSON.parse(x.workers) as string[]) : [],
        workersJSON: x.workersJSON!,
        address: x.address!,
        cityName: x.cityName!,
        citySQLServerId: x.citySQLServerId!,
        countrySQLServerId: x.countrySQLServerId!,
        postalZip: x.postalZip!,
        provStateSQLServerId: x.provStateSQLServerId!,
        dataHubVersion: x.dataHubVersion!,
      })) ?? [],
    [projects],
  );

  const mappedProvStates = useMemo(
    () =>
      provStates.map(x => ({
        _id: x._id!,
        SQLServerId: x.SQLServerId!,
        countrySQLServerId: x.countrySQLServerId!,
        provState: x.provState!,
        provStateCode: x.provStateCode!,
      })) ?? [],
    [provStates],
  );

  const mappedRoles = useMemo(
    () =>
      roles.map(x => ({
        _id: x._id!,
        name: x.name!,
        description: x.description!,
        sortOrder: x.sortOrder!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [roles],
  );

  const mappedSeverityLevels = useMemo(
    () =>
      severityLevels.map(x => ({
        _id: x._id!,
        name: x.name!,
        value: x.value!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [severityLevels],
  );

  const mappedShifts = useMemo(
    () =>
      shifts.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [shifts],
  );

  const mappedTasks = useMemo(
    () =>
      tasks.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        SQLServerId: x.SQLServerId!,
        dataHubVersion: x.dataHubVersion!,
        taskTypeSQLServerId: x.taskTypeSQLServerId!,
      })) ?? [],
    [tasks],
  );

  const mappedTaskTypes = useMemo(
    () =>
      taskTypes.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [taskTypes],
  );

  const mappedTemplateCategories = useMemo(
    () =>
      templateCategories.map(x => ({
        _id: x._id!,
        name: x.name!,
        listTemplateIds: x.listTemplateIds
          ? (JSON.parse(x.listTemplateIds) as string[])
          : []!,
        actionTemplateIds: x.actionTemplateIds
          ? (JSON.parse(x.actionTemplateIds) as string[])
          : []!,
      })) ?? [],
    [templateCategories],
  );

  const mappedTemplateGroups = useMemo(
    () =>
      templateGroups.map(x => ({
        _id: x._id!,
        name: x.name!,
        templateIds: x.templateIds
          ? (JSON.parse(x.templateIds) as string[])
          : []!,
      })) ?? [],
    [templateGroups],
  );

  const mappedTemplates = useMemo(() => {
    let orgTemplateGroupIds: string[] = mappedOrgTemplateGroups
      .map(x => x.templateGroupIds!)
      .flat();

    let filteredTemplateIds = mappedTemplateGroups
      .filter(x => orgTemplateGroupIds.includes(x._id!))
      .map(x => x.templateIds!)
      .flat();

    let filteredTemplates = templates.filter(x =>
      filteredTemplateIds.includes(x._id!),
    );

    return (
      filteredTemplates.map(x => ({
        _id: x._id!,
        name: x.name!,
        templateCategory: x.templateCategory
          ? (JSON.parse(x.templateCategory) as Template_templateCategory)
          : null!,
        templateType: x.templateType
          ? (JSON.parse(x.templateType) as Template_TemplateType)
          : null!,
        iconSvg: x.iconSvg!,
        row: x.row!,
        order: x.order!,
      })) ?? []
    );
  }, [mappedOrgTemplateGroups, templates, mappedTemplateGroups, templates]);

  const mappedTemplateTypes = useMemo(
    (): Template_Type[] =>
      templateTypes.map(x => ({
        _id: x._id!,
        name: x.name!,
      })) ?? [],
    [templateTypes],
  );

  const mappedTemplateVersions = useMemo((): TemplateVersion[] => {
    let orgTemplateGroupIds = mappedOrgTemplateGroups
      .map(x => x.templateGroupIds)
      .flat();
    let filteredTemplateIds = mappedTemplateGroups
      .filter(x => orgTemplateGroupIds.includes(x._id!))
      .map(x => x.templateIds)
      .flat();

    let filteredTemplates = templateVersions.filter(x =>
      filteredTemplateIds.includes(x.templateId!),
    );

    return (
      filteredTemplates.map(x => ({
        _id: x._id!,
        name: x.name!,
        displayName: x.displayName!,
        templateId: x.templateId!,
        version: x.version!,
        pages: x.pages ? (JSON.parse(x.pages) as TemplateVersion_pages[]) : [],
        conditions: x.conditions
          ? (JSON.parse(x.conditions) as TemplateVersion_conditions[])
          : [],
      })) ?? []
    );
  }, [templateVersions, mappedTemplateGroups, mappedOrgTemplateGroups]);

  const mappedTemplateVersionConversions = useMemo(
    (): TemplateVersionsConversion[] =>
      templateVersionConversions.map(x => ({
        _id: x._id!,
        fromTemplateVersionId: x.fromTemplateVersionId!,
        toTemplateVersionId: x.toTemplateVersionId!,
        conversions: x.conversions
          ? (JSON.parse(
              x.conversions,
            ) as TemplateVersionsConversion_conversion[])
          : [],
      })) ?? [],
    [templateVersionConversions],
  );

  const mappedTrainingTypes = useMemo(
    (): TrainingType[] =>
      trainingTypes.map(x => ({
        _id: x._id!,
        name: x.name!,
        partition: x.partition!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        sortOrder: x.sortOrder!,
        dataHubVersion: x.dataHubVersion!,
        SQLServerId: x.SQLServerId!,
      })) ?? [],
    [trainingTypes],
  );

  const mappedSQLUsers = useMemo(
    (): SQLUser[] =>
      SQLUsers.map(x => ({
        _id: x._id!,
        firstName: x.firstName!,
        lastName: x.lastName!,
        partition: x.partition!,
        email: x.email!,
        ehsId: x.ehsId!,
        isActive: !!x.isActive,
        isDeleted: !!x.isDeleted,
        SQLServerId: x.SQLServerId!,
        SQLServerPersonId: x.SQLServerPersonId!,
        SQLServerInvitedByPersonId: x.SQLServerInvitedByPersonId!,
        dataHubVersion: x.dataHubVersion!,
        statusObj: x.statusObj
          ? (JSON.parse(x.statusObj) as SQLUser_statusObj)
          : null,
        SQLServerRoleIds: x.SQLServerRoleIds
          ? (JSON.parse(x.SQLServerRoleIds) as string[])
          : [],
        createDateTimeStamp: new Date(x.createDateTimeStamp!),
      })) ?? [],
    [SQLUsers],
  );

  const mappedUserStatuses = useMemo(
    (): UserStatus[] =>
      userStatuses.map(x => ({
        _id: x._id!,
        name: x.name!,
      })) ?? [],
    [userStatuses],
  );

  const mappedSubmissionLinks = useMemo(
    (): SubmissionLink[] =>
      submissionLinks.map(x => ({
        _id: x._id!,
        partition: x.partition!,
        parentSubmissionId: x.parentSubmissionId!,
        childSubmissionId: x.childSubmissionId!,
      })) ?? [],
    [submissionLinks],
  );

  const mappedsubmissionStatuses = useMemo(
    (): SubmissionStatus[] =>
      submissionStatuses.map(x => ({
        _id: x._id!,
        name: x.name!,
      })) ?? [],
    [submissionStatuses],
  );

  const mappedSubmissions = useMemo(
    (): Submission[] =>
      submissions.map(x => ({
        _id: x._id!,
        partition: x.partition!,
        createdBy: x.createdBy!,
        createDateTimeStamp: new Date(x.createDateTimeStamp!),
        submittedBy: x.submittedBy ?? undefined,
        submittedDateTimeStamp: x.submittedDateTimeStamp
          ? new Date(x.submittedDateTimeStamp)
          : undefined,
        updatedBy: x.updatedBy!,
        updateDateTimeStamp: new Date(x.updateDateTimeStamp!),
        SQLServerId: x.SQLServerId!,
        templateVersion: JSON.parse(
          x.templateVersion!,
        ) as Submission_templateVersion,
        templateType: JSON.parse(x.templateType!) as Submission_templateType,
        metadataJSON: x.metadataJSON!,
        answers: x.answers
          ? (JSON.parse(x.answers) as Submission_answer[])
          : [],
        submissionStatus: JSON.parse(
          x.submissionStatus!,
        ) as Submission_submissionStatus,
        dataHubVersion: x.dataHubVersion!,
        attachmentsUploaded: !!x.attachmentsUploaded,
        submissionStatusLock: x.submissionStatusLock!,
        displayId: x.displayId,
      })) ?? [],
    [submissions],
  );

  const mappedSubmissionBundles = useMemo(
    (): SubmissionBundle[] =>
      submissionBundles.map(x => ({
        _id: x._id!,
        createDateTimeStamp: new Date(x.createDateTimeStamp!),
        createdBy: x.createdBy!,
        externalOrgId: x.externalOrgId!,
        externalRecipients: x.externalRecipients
          ? (JSON.parse(x.externalRecipients) as string[])
          : [],
        internalRecipients: x.internalRecipients
          ? (JSON.parse(x.internalRecipients) as string[])
          : [],
        auditSubmissionId: x.auditSubmissionId!,
        isAudit: !!x.isAudit,
        name: x.name!,
        partition: x.partition!,
        status: x.status!,
        submitDateTimeStamp: new Date(x.submitDateTimeStamp!),
        dataHubVersion: x.dataHubVersion!,
        attachmentsUploaded: !!x.attachmentsUploaded!,
        bundleAttachments: x.bundleAttachments!,
        SQLServerId: x.SQLServerId!,
        submissions: x.submissions
          ? (JSON.parse(x.submissions) as SubmissionBundle_submissions[])
          : [],
      })) ?? [],
    [submissionBundles],
  );

  const mappedSubmissionRelations = useMemo(
    (): SubmissionRelation[] =>
      submissionRelations.map(x => ({
        _id: x._id!,
        partition: x.partition!,
        submissionId: x.submissionId!,
        relationId: x.relationId!,
        relationType: x.relationType!,
      })) ?? [],
    [submissionRelations],
  );

  const mappedSubmissionTrainings = useMemo(
    (): SubmissionTraining[] =>
      submissionTrainings.map(x => ({
        _id: x._id!,
        partition: x.partition!,
        createdBy: x.createdBy!,
        createDateTimeStamp: new Date(x.createDateTimeStamp!),
        submittedBy: x.submittedBy ?? undefined,
        submittedDateTimeStamp: x.submittedDateTimeStamp
          ? new Date(x.submittedDateTimeStamp)
          : undefined,
        updatedBy: x.updatedBy!,
        updateDateTimeStamp: new Date(x.updateDateTimeStamp!),
        SQLServerId: x.SQLServerId!,
        person: x.person
          ? (JSON.parse(x.person) as SubmissionTraining_person)
          : null,
        trainingType: x.trainingType
          ? (JSON.parse(x.trainingType) as SubmissionTraining_trainingType)
          : null,
        certificateType: x.certificateType
          ? (JSON.parse(
              x.certificateType,
            ) as SubmissionTraining_certificateType)
          : null,
        templateVersion: x.templateVersion
          ? (JSON.parse(
              x.templateVersion,
            ) as SubmissionTraining_templateVersion)
          : null,
        templateType: x.templateType
          ? (JSON.parse(x.templateType) as SubmissionTraining_templateType)
          : null,
        metadataJSON: x.metadataJSON!,
        answers: x.answers
          ? (JSON.parse(x.answers) as SubmissionTraining_answer[])
          : [],
        submissionStatus: x.submissionStatus
          ? (JSON.parse(
              x.submissionStatus,
            ) as SubmissionTraining_submissionStatus)
          : null,
        submissionStatusLock: x.submissionStatusLock,
        dataHubVersion: x.dataHubVersion!,
        attachmentsUploaded: !!x.attachmentsUploaded,
      })) ?? [],
    [submissionTrainings],
  );

  const getPerson = async (personId: string) => {
    let result = await powerSync.get<Database['People']>(
      'SELECT * FROM People  WHERE id = ?',
      [personId],
    );

    let mappedResult = {
      _id: result._id!,
      firstName: result.firstName!,
      lastName: result.lastName!,
      email: result.email!,
      phone: result.phone!,
      fax: result.fax!,
      phoneDirect: result.phoneDirect!,
      supervisorId: result.supervisorId!,
      supervisorSQLServerId: result.supervisorSQLServerId!,
      partition: result.partition!,
      isActive: !!result.isActive,
      isDeleted: !!result.isDeleted,
      SQLServerId: result.SQLServerId!,
      dataHubVersion: result.dataHubVersion!,
      jobId: result.jobId!,
      jobSQLServerId: result.jobSQLServerId!,
      peopleTypeId: result.peopleTypeId!,
      peopleTypeSQLServerId: result.peopleTypeSQLServerId!,
      photo: result.photo!,
      photoThumbnail: result.photoThumbnail!,
      hireDate: result.hireDate ? new Date(result.hireDate!) : null,
      inactiveDate: result.inactiveDate ? new Date(result.inactiveDate!) : null,
      employeeNumber: result.employeeNumber!,
    } as Person;
    return mappedResult;
  };

  const getProject = async (projectId: string) => {
    let result = await powerSync.get<Database['Projects']>(
      'SELECT * FROM Projects  WHERE id = ?',
      [projectId],
    );

    let mappedResult = {
      _id: result._id!,
      name: result.name!,
      projectManagerId: result.projectManagerId!,
      projectManagerSQLServerId: result.projectManagerSQLServerId!,
      partition: result.partition!,
      isActive: !!result.isActive,
      isDeleted: !!result.isDeleted,
      location: result.location
        ? (JSON.parse(result.location) as Project_location)
        : null!,
      activeStartDate: result.activeStartDate
        ? new Date(result.activeStartDate)
        : null,
      activeEndDate: result.activeEndDate
        ? new Date(result.activeEndDate)
        : null,
      SQLServerId: result.SQLServerId!,
      safetyResourceId: result.safetyResourceId!,
      safetyResourceSQLServerId: result.safetyResourceSQLServerId!,
      siteSupervisorId: result.siteSupervisorId!,
      siteSupervisorSQLServerId: result.siteSupervisorSQLServerId!,
      workers: result.workers ? (JSON.parse(result.workers) as string[]) : [],
      workersJSON: result.workersJSON!,
      address: result.address!,
      cityName: result.cityName!,
      citySQLServerId: result.citySQLServerId!,
      countrySQLServerId: result.countrySQLServerId!,
      postalZip: result.postalZip!,
      provStateSQLServerId: result.provStateSQLServerId!,
      dataHubVersion: result.dataHubVersion!,
    } as Project;
    return mappedResult;
  };

  const getEquipmentVehicle = async (id: string) => {
    let result = await powerSync.get<Database['EquipmentVehicleProfiles']>(
      'SELECT * FROM EquipmentVehicleProfiles WHERE id = ?',
      [id],
    );

    let mappedResult = {
      _id: result._id!,
      name: result.name!,
      number: result.number!,
      description: result.description!,
      licensePlate: result.licensePlate!,
      distance: result.distance!,
      ownershipType: result.ownershipType!,
      status: result.status!,
      photo: result.photo!,
      photos: result.photos!,
      partition: result.partition!,
      dataHubVersion: result.dataHubVersion!,
      SQLServerId: result.SQLServerId!,
    } as EquipmentVehicleProfile;

    return mappedResult;
  };

  const getEquipmentHeavy = async (id: string) => {
    let result = await powerSync.get<Database['EquipmentHeavyProfiles']>(
      'SELECT * FROM EquipmentHeavyProfiles WHERE id = ?',
      [id],
    );

    let mappedResult = {
      _id: result._id!,
      name: result.name!,
      number: result.number!,
      description: result.description!,
      hoursUsed: result.hoursUsed!,
      ownershipType: result.ownershipType!,
      status: result.status!,
      photo: result.photo!,
      photos: result.photos!,
      partition: result.partition!,
      dataHubVersion: result.dataHubVersion!,
      SQLServerId: result.SQLServerId!,
    } as EquipmentHeavyProfile;

    return mappedResult;
  };

  const getEquipmentOther = async (id: string) => {
    let result = await powerSync.get<Database['EquipmentOtherProfiles']>(
      'SELECT * FROM EquipmentOtherProfiles WHERE id = ?',
      [id],
    );

    let mappedResult = {
      _id: result._id!,
      name: result.name!,
      number: result.number!,
      description: result.description!,
      ownershipType: result.ownershipType!,
      status: result.status!,
      photo: result.photo!,
      photos: result.photos!,
      partition: result.partition!,
      dataHubVersion: result.dataHubVersion!,
      SQLServerId: result.SQLServerId!,
    } as EquipmentOtherProfile;

    return mappedResult;
  };

  const getTemplate = async (id: string) => {
    let result = await powerSync.get<Database['Templates']>(
      'SELECT * FROM Templates WHERE id = ?',
      [id],
    );

    let mappedResult = {
      _id: result._id!,
      name: result.name!,
      templateCategory: result.templateCategory
        ? (JSON.parse(result.templateCategory) as Template_templateCategory)
        : null!,
      templateType: result.templateType
        ? (JSON.parse(result.templateType) as Template_TemplateType)
        : null!,
      iconSvg: result.iconSvg!,
      row: result.row!,
      order: result.order!,
    } as Template;

    return mappedResult;
  };

  const getTemplateVersion = async (id: string) => {
    let result = await powerSync.get<Database['TemplateVersions']>(
      'SELECT * FROM TemplateVersions WHERE id = ?',
      [id],
    );

    let mappedResult = {
      _id: result._id!,
      name: result.name!,
      displayName: result.displayName!,
      templateId: result.templateId!,
      version: result.version!,
      pages: result.pages
        ? (JSON.parse(result.pages) as TemplateVersion_pages[])
        : [],
      conditions: result.conditions
        ? (JSON.parse(result.conditions) as TemplateVersion_conditions[])
        : [],
    } as TemplateVersion;

    return mappedResult;
  };

  const getSubmissionBundle = async (submissionBundleId: string) => {
    let result = await powerSync.get<Database['SubmissionBundles']>(
      'SELECT * FROM SubmissionBundles WHERE id = ?',
      [submissionBundleId],
    );

    let mappedResult = {
      _id: result._id!,
      createDateTimeStamp: new Date(result.createDateTimeStamp!),
      createdBy: result.createdBy!,
      externalOrgId: result.externalOrgId!,
      externalRecipients: result.externalRecipients
        ? (JSON.parse(result.externalRecipients) as string[])
        : [],
      internalRecipients: result.internalRecipients
        ? (JSON.parse(result.internalRecipients) as string[])
        : [],
      auditSubmissionId: result.auditSubmissionId!,
      isAudit: !!result.isAudit,
      name: result.name!,
      partition: result.partition!,
      status: result.status!,
      submitDateTimeStamp: new Date(result.submitDateTimeStamp!),
      dataHubVersion: result.dataHubVersion!,
      attachmentsUploaded: !!result.attachmentsUploaded!,
      bundleAttachments: result.bundleAttachments!,
      SQLServerId: result.SQLServerId!,
      submissions: result.submissions
        ? (JSON.parse(result.submissions) as SubmissionBundle_submissions[])
        : [],
    } as SubmissionBundle;
    return mappedResult;
  };

  const getSubmission = async (submissionId: string) => {
    let result = await powerSync.get<Database['Submissions']>(
      'SELECT * FROM Submissions WHERE id = ?',
      [submissionId],
    );

    let mappedResult = {
      _id: result._id!,
      partition: result.partition!,
      createdBy: result.createdBy!,
      createDateTimeStamp: new Date(result.createDateTimeStamp!),
      submittedBy: result.submittedBy ?? undefined,
      submittedDateTimeStamp: result.submittedDateTimeStamp
        ? new Date(result.submittedDateTimeStamp)
        : undefined,
      updatedBy: result.updatedBy!,
      updateDateTimeStamp: new Date(result.updateDateTimeStamp!),
      SQLServerId: result.SQLServerId!,
      templateVersion: JSON.parse(
        result.templateVersion!,
      ) as Submission_templateVersion,
      templateType: JSON.parse(result.templateType!) as Submission_templateType,
      metadataJSON: result.metadataJSON!,
      answers: result.answers
        ? (JSON.parse(result.answers) as Submission_answer[])
        : [],
      submissionStatus: JSON.parse(
        result.submissionStatus!,
      ) as Submission_submissionStatus,
      dataHubVersion: result.dataHubVersion!,
      attachmentsUploaded: !!result.attachmentsUploaded,
      submissionStatusLock: result.submissionStatusLock!,
      displayId: result.displayId,
    } as Submission;

    return mappedResult;
  };

  const getSubmissionTraining = async (submissionTrainingId: string) => {
    let result = await powerSync.get<Database['SubmissionTrainings']>(
      'SELECT * FROM SubmissionTrainings WHERE id = ?',
      [submissionTrainingId],
    );

    let mappedResult = {
      _id: result._id!,
      id: result.id!,
      partition: result.partition!,
      createdBy: result.createdBy!,
      createDateTimeStamp: new Date(result.createDateTimeStamp!),
      submittedBy: result.submittedBy ?? undefined,
      submittedDateTimeStamp: result.submittedDateTimeStamp
        ? new Date(result.submittedDateTimeStamp)
        : undefined,
      updatedBy: result.updatedBy!,
      updateDateTimeStamp: new Date(result.updateDateTimeStamp!),
      SQLServerId: result.SQLServerId!,
      person: result.person
        ? (JSON.parse(result.person) as SubmissionTraining_person)
        : null,
      trainingType: result.trainingType
        ? (JSON.parse(result.trainingType) as SubmissionTraining_trainingType)
        : null,
      certificateType: result.certificateType
        ? (JSON.parse(
            result.certificateType,
          ) as SubmissionTraining_certificateType)
        : null,
      templateVersion: result.templateVersion
        ? (JSON.parse(
            result.templateVersion,
          ) as SubmissionTraining_templateVersion)
        : null,
      templateType: result.templateType
        ? (JSON.parse(result.templateType) as SubmissionTraining_templateType)
        : null,
      metadataJSON: result.metadataJSON!,
      answers: result.answers
        ? (JSON.parse(result.answers) as SubmissionTraining_answer[])
        : [],
      submissionStatus: result.submissionStatus
        ? (JSON.parse(
            result.submissionStatus,
          ) as SubmissionTraining_submissionStatus)
        : null,
      submissionStatusLock: result.submissionStatusLock,
      dataHubVersion: result.dataHubVersion!,
      attachmentsUploaded: !!result.attachmentsUploaded,
    } as SubmissionTraining;

    return mappedResult;
  };

  const existSubmissionInBundle = useCallback(
    (submissionId: string) => {
      let exists = mappedSubmissionBundles.some(x =>
        x.submissions?.some(y => y.submissionId === submissionId),
      );
      return exists;
    },
    [submissions, submissionTrainings, submissionBundles],
  );

  const powersyncReady: boolean =
    powersyncStatus.connected &&
    powerSync.ready &&
    (powersyncStatus.hasSynced ?? false) &&
    !loadingPeople &&
    !loadingTemplates &&
    !loadingTemplateVersions &&
    !loadingSubmissions &&
    !loadingSubmissionStatuses &&
    !loadingSubmissionTrainings &&
    !loadingSubmissionBundles &&
    !loadingEquipmentVehicleProfiles &&
    !loadingEquipmentHeavyProfiles &&
    !loadingEquipmentOtherProfiles &&
    !loadingDocuments &&
    !loadingDocumentTypes &&
    !loadingPermissions;

  const value = {
    animalTypes: mappedAnimalTypes,
    bodyParts: mappedBodyParts,
    certificationTypes: mappedCertificationTypes,
    cities: mappedCities,
    countries,
    classificationUnits: mappedClassificationUnits,
    crews: mappedCrews,
    departments: mappedDepartments,
    dispositions: mappedDispositions,
    documents: mappedDocuments,
    documentTypes: mappedDocumentTypes,
    equipmentHeavyProfiles: mappedEquipmentHeavyProfiles,
    equipmentOtherProfiles: mappedEquipmentOtherProfiles,
    equipmentProfileTypes: mappedEquipmentProfileTypes,
    equipments: mappedEquipments,
    equipmentTypes: mappedEquipmentTypes,
    equipmentVehicleProfiles: mappedEquipmentVehicleProfiles,
    hazardTypes: mappedHazardTypes,
    incidentClassifications: mappedIncidentClassifications,
    incidentProbabilities: mappedIncidentProbabilities,
    incidentTypes: mappedIncidentTypes,
    jobs: mappedJobs,
    locations: mappedLocations,
    locationTypes: mappedLocationTypes,
    mechanisms: mappedMechanisms,
    meetingTypes: mappedMeetingTypes,
    natures: mappedNatures,
    OIICS_BodyParts: mappedOIICS_BodyParts,
    OIICS_Natures: mappedOIICS_Natures,
    OIICS_Sources: mappedOIICS_Sources,
    operatingConditions: mappedOperatingConditions,
    organisations: mappedOrganisations,
    orgContractorNotificationRecipients:
      mappedOrgContractorNotificationRecipients,
    orgRelations: mappedOrgRelations,
    orgStorages: mappedOrgStorages,
    orgTemplateGroups: mappedOrgTemplateGroups,
    people: mappedPeople,
    peopleTypes: mappedPeopleTypes,
    permissions: mappedPermissions,
    projects: mappedProjects,
    provStates: mappedProvStates,
    roles: mappedRoles,
    severityLevels: mappedSeverityLevels,
    shifts: mappedShifts,
    SQLUsers: mappedSQLUsers,
    submissionBundles: mappedSubmissionBundles,
    submissionRelations: mappedSubmissionRelations,
    submissions: mappedSubmissions,
    submissionStatuses: mappedsubmissionStatuses,
    submissionTrainings: mappedSubmissionTrainings,
    tasks: mappedTasks,
    taskTypes: mappedTaskTypes,
    templateCategories: mappedTemplateCategories,
    templateGroups: mappedTemplateGroups,
    templates: mappedTemplates,
    templateTypes: mappedTemplateTypes,
    templateVersionConversions: mappedTemplateVersionConversions,
    templateVersions: mappedTemplateVersions,
    trainingTypes: mappedTrainingTypes,
    userStatuses: mappedUserStatuses,
    submissionLinks: mappedSubmissionLinks,
    getPerson: getPerson,
    getProject: getProject,
    getEquipmentVehicle: getEquipmentVehicle,
    getEquipmentHeavy: getEquipmentHeavy,
    getEquipmentOther: getEquipmentOther,
    getTemplate: getTemplate,
    getTemplateVersion: getTemplateVersion,
    getSubmissionBundle: getSubmissionBundle,
    getSubmission: getSubmission,
    getSubmissionTraining: getSubmissionTraining,
    existSubmissionInBundle: existSubmissionInBundle,
    powersyncReady: powersyncReady,
  };

  return (
    <PowerSyncDataContext.Provider value={value}>
      {children}
    </PowerSyncDataContext.Provider>
  );
};

const usePowerSyncData = () => {
  const powerSyncDataContext = useContext(PowerSyncDataContext);

  if (powerSyncDataContext === null)
    throw new Error(
      'usePowerSyncData() called outside of a PowerSyncDataProvider?',
    );

  return powerSyncDataContext;
};

export {PowerSyncDataProvider, PowerSyncDataContext, usePowerSyncData};
